import { useState } from "react";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { ArrowLeft, ArrowRight, Check, Sprout, TrendingUp, Users, Cpu, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { Seo } from "@/components/seo/Seo";
import { cn } from "@/lib/utils";

const PERSONAS = [
  { id: "explorer", icon: Sprout },
  { id: "series_a_plus", icon: TrendingUp },
  { id: "seed_founder", icon: Users },
  { id: "bootstrapper", icon: Cpu },
] as const;

type PersonaId = (typeof PERSONAS)[number]["id"];

const OnboardingPersona = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<PersonaId | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSkip = async () => {
    const { data: u } = await supabase.auth.getUser();
    if (u.user) {
      await supabase
        .from("profiles")
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq("id", u.user.id);
    }
    navigate("/dashboard", { replace: true });
  };

  const handleContinue = async () => {
    if (!selected) return;
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      navigate("/login", { replace: true });
      return;
    }
    const { error } = await supabase.from("profiles").update({ persona: selected }).eq("id", u.user.id);
    setSaving(false);
    if (error) {
      toast.error(t("onboarding.errors.saveFailed"));
      return;
    }
    navigate("/onboarding/tracks");
  };

  return (
    <>
      <Seo title={t("onboarding.persona.seoTitle")} />
      <OnboardingShell step={1} showSkip onSkip={handleSkip}>
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {t("onboarding.persona.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            {t("onboarding.persona.subtitle")}
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2">
          {PERSONAS.map(({ id, icon: Icon }) => {
            const isSel = selected === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setSelected(id)}
                aria-pressed={isSel}
                className={cn(
                  "group relative rounded-2xl border-2 p-6 text-left transition-all",
                  isSel
                    ? "border-primary bg-primary/5 shadow-lg -translate-y-0.5"
                    : "border-border bg-card hover:border-primary/40 hover:bg-muted/30",
                )}
              >
                <div
                  className={cn(
                    "absolute right-4 top-4 grid h-6 w-6 place-items-center rounded-full border-2 transition-colors",
                    isSel ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/30",
                  )}
                >
                  {isSel ? <Check className="h-3.5 w-3.5" /> : null}
                </div>
                <div
                  className={cn(
                    "grid h-12 w-12 place-items-center rounded-xl transition-colors",
                    isSel ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
                  )}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{t(`onboarding.persona.options.${id}.label`)}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {t(`onboarding.persona.options.${id}.desc`)}
                </p>
                <p className="mt-3 text-xs font-mono uppercase tracking-wider text-muted-foreground/70">
                  {t(`onboarding.persona.options.${id}.example`)}
                </p>
              </button>
            );
          })}
        </div>

        <div className="mx-auto mt-10 flex max-w-3xl items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} disabled={saving}>
            <ArrowLeft className="mr-1 h-4 w-4" /> {t("common.back")}
          </Button>
          <div className="flex items-center gap-3">
            {!selected && (
              <span className="hidden text-xs font-mono text-muted-foreground sm:inline">
                {t("onboarding.persona.hint")}
              </span>
            )}
            <Button onClick={handleContinue} disabled={!selected || saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {t("common.next")} <ArrowRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </OnboardingShell>
    </>
  );
};

export default OnboardingPersona;
